import type { Counter, Histogram, Meter } from '@opentelemetry/api';
import type { IMetricsProvider } from '../../metrics/types';
import type { IWebVitalMetric } from '../../types';
import {
  flushOpenTelemetry,
  getOtelMeter,
  shutdownOpenTelemetry,
} from './initOpenTelemetry';

function sanitizeName(name: string): string {
  return name.replace(/[^a-zA-Z0-9._\-/]/g, '_');
}

export class OtelMetricsProvider implements IMetricsProvider {
  private meter: Meter | null = null;
  private readonly counters = new Map<string, Counter>();
  private readonly histograms = new Map<string, Histogram>();
  private readonly gaugeValues = new Map<string, number>();
  private readonly gaugeRegistered = new Set<string>();

  private getMeter(): Meter {
    if (!this.meter) this.meter = getOtelMeter();
    return this.meter;
  }

  private counter(name: string): Counter {
    const key = sanitizeName(name);
    let c = this.counters.get(key);
    if (!c) {
      c = this.getMeter().createCounter(key);
      this.counters.set(key, c);
    }
    return c;
  }

  private histogram(name: string): Histogram {
    const key = sanitizeName(name);
    let h = this.histograms.get(key);
    if (!h) {
      h = this.getMeter().createHistogram(key, { unit: 'ms' });
      this.histograms.set(key, h);
    }
    return h;
  }

  increment(name: string, value = 1): void {
    this.counter(name).add(value);
  }

  gauge(name: string, value: number): void {
    const key = sanitizeName(name);
    this.gaugeValues.set(key, value);
    if (this.gaugeRegistered.has(key)) return;

    // ponytail: ObservableGauge com último valor — sync gauge se API estabilizar
    this.getMeter()
      .createObservableGauge(key)
      .addCallback((result) => {
        const last = this.gaugeValues.get(key);
        if (last !== undefined) result.observe(last);
      });
    this.gaugeRegistered.add(key);
  }

  timing(name: string, durationMs: number): void {
    this.histogram(`${name}.duration_ms`).record(durationMs);
  }

  recordWebVital(metric: IWebVitalMetric): void {
    this.histogram(`web_vital.${metric.name}`).record(metric.value, {
      rating: metric.rating ?? 'unknown',
      id: metric.id ?? '',
    });
  }

  async flush(): Promise<void> {
    await flushOpenTelemetry();
  }

  async shutdown(): Promise<void> {
    await shutdownOpenTelemetry();
  }
}
